import { useHandler } from "@/hooks";
import type { ApiResponse } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import type { FpMessage } from "../../../schemas/codegen";
import { useFilesystemStore, useMessageStore } from "../../state/stores";
import { api } from "../api/api";

type MessagesResponse = ApiResponse<{
  messages: FpMessage[];
}>;

export function useMessageCacheSync(activeProjectId: string | undefined) {
  const { setMessages, clearProjectData, setLoading } = useMessageStore(
    useShallow((state) => ({
      setLoading: state.setLoading,
      setMessages: state.setMessages,
      clearProjectData: state.clearProjectData,
    })),
  );

  const { setSnapshots, setStagedFiles, clearFilesystem } = useFilesystemStore(
    useShallow((state) => ({
      setSnapshots: state.setSnapshots,
      setStagedFiles: state.setStagedFiles,
      clearFilesystem: state.clearFilesystem,
    })),
  );

  const previousProjectIdRef = useRef<string | undefined>(undefined);

  const fetchAndSyncFilesystemData = useHandler(async (projectId: string) => {
    try {
      const [snapshotsResponse, filesResponse] = await Promise.all([
        api.listProjectSnapshots(projectId),
        api.getCurrentFileTree(projectId),
      ]);

      if (snapshotsResponse.data?.snapshots) {
        setSnapshots(snapshotsResponse.data.snapshots);
      }

      if (filesResponse.data?.files) {
        setStagedFiles(filesResponse.data.files);
      }
    } catch (error) {
      console.warn("Failed to load filesystem data:", error);
    }
  });

  const { data: cachedMessages, refetch } = useQuery<MessagesResponse>({
    queryKey: ["messages", activeProjectId],
    queryFn: async () => {
      setLoading(true);
      if (!activeProjectId) {
        return { data: { messages: [] } };
      }

      // Clean parallel loading - fetch messages and filesystem data together
      const [messagesResult] = await Promise.all([
        api.listMessages(activeProjectId),
        fetchAndSyncFilesystemData(activeProjectId),
      ]);

      setLoading(false);
      return messagesResult;
    },
    enabled: !!activeProjectId,
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    staleTime: 0, // Always consider data stale to trigger background refetches
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Handle project switching and cache updates
  useEffect(() => {
    const isProjectSwitch =
      previousProjectIdRef.current !== undefined &&
      activeProjectId !== previousProjectIdRef.current;

    if (isProjectSwitch) {
      if (!cachedMessages?.data.messages) {
        clearProjectData();
        clearFilesystem();
        return;
      }

      // For project switches, directly set the messages
      setMessages(cachedMessages.data.messages);
      // Trigger a background refetch (which will also refresh filesystem data)
      refetch();
    } else {
      if (!cachedMessages?.data.messages) {
        return;
      }

      // For regular updates, merge with existing messages
      const currentMessages = useMessageStore.getState().messages;
      const currentMessageMap = new Map(
        currentMessages.map((msg: FpMessage) => [msg.id, msg]),
      );

      const mergedMessages = cachedMessages.data.messages.map(
        (cachedMsg: FpMessage) => {
          const currentMsg = currentMessageMap.get(cachedMsg.id);
          if (
            currentMsg &&
            // TODO: Add more robust comparison logic
            // For now: more content equals newer.
            currentMsg.content.length > cachedMsg.content.length
          ) {
            return currentMsg;
          }
          return cachedMsg;
        },
      );

      const newMessages = currentMessages.filter(
        (msg: FpMessage) =>
          !cachedMessages.data.messages.some(
            (cached: FpMessage) => cached.id === msg.id,
          ),
      );

      setMessages([...mergedMessages, ...newMessages]);
    }

    previousProjectIdRef.current = activeProjectId;
  }, [
    activeProjectId,
    cachedMessages,
    setMessages,
    refetch,
    clearProjectData,
    clearFilesystem,
  ]);

  return {
    refetch, // Expose refetch for manual refreshes if needed
  };
}
